import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { flashcardApi } from '../api/flashcardApi';
import type {
  DeckCreate,
  DeckUpdate,
  CardCreate,
  CardUpdate,
  VocabularyDeckCreate,
  RecordAnswerRequest,
  StartSessionRequest,
} from '../types/flashcard';

// ==================== Query Keys ====================
export const flashcardKeys = {
  all: ['flashcards'] as const,
  decks: () => [...flashcardKeys.all, 'decks'] as const,
  deck: (id: string) => [...flashcardKeys.decks(), id] as const,
  cards: (deckId: string) => [...flashcardKeys.all, 'cards', deckId] as const,
  card: (id: string) => [...flashcardKeys.all, 'card', id] as const,
  sessions: () => [...flashcardKeys.all, 'sessions'] as const,
  session: (id: string) => [...flashcardKeys.sessions(), id] as const,
  progress: (deckId: string) => [...flashcardKeys.all, 'progress', deckId] as const,
  cardProgress: (cardId: string) => [...flashcardKeys.all, 'cardProgress', cardId] as const,
  statistics: () => [...flashcardKeys.all, 'statistics'] as const,
  dueCards: (deckId?: string) => [...flashcardKeys.all, 'due', deckId] as const,
};

// ==================== Deck Hooks ====================

export function useDecks(params?: { deck_type?: string; theme?: string; include_public?: boolean }) {
  return useQuery({
    queryKey: [...flashcardKeys.decks(), params],
    queryFn: () => flashcardApi.getDecks(params),
  });
}

export function useDeck(deckId: string | undefined) {
  return useQuery({
    queryKey: flashcardKeys.deck(deckId!),
    queryFn: () => flashcardApi.getDeck(deckId!),
    enabled: !!deckId,
  });
}

export function useCreateDeck() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DeckCreate) => flashcardApi.createDeck(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: flashcardKeys.decks() });
      notifications.show({
        title: 'Success',
        message: 'Deck created successfully',
        color: 'teal',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create deck',
        color: 'red',
      });
    },
  });
}

export function useUpdateDeck(deckId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DeckUpdate) => flashcardApi.updateDeck(deckId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: flashcardKeys.deck(deckId) });
      queryClient.invalidateQueries({ queryKey: flashcardKeys.decks() });
      notifications.show({
        title: 'Success',
        message: 'Deck updated successfully',
        color: 'teal',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update deck',
        color: 'red',
      });
    },
  });
}

export function useDeleteDeck() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deckId: string) => flashcardApi.deleteDeck(deckId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: flashcardKeys.decks() });
      notifications.show({
        title: 'Success',
        message: 'Deck deleted successfully',
        color: 'teal',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete deck',
        color: 'red',
      });
    },
  });
}

// ==================== Card Hooks ====================

export function useCards(deckId: string | undefined) {
  return useQuery({
    queryKey: flashcardKeys.cards(deckId!),
    queryFn: () => flashcardApi.getCards(deckId!),
    enabled: !!deckId,
  });
}

export function useCard(cardId: string | undefined) {
  return useQuery({
    queryKey: flashcardKeys.card(cardId!),
    queryFn: () => flashcardApi.getCard(cardId!),
    enabled: !!cardId,
  });
}

export function useCreateCard(deckId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CardCreate) => flashcardApi.createCard(deckId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: flashcardKeys.cards(deckId) });
      queryClient.invalidateQueries({ queryKey: flashcardKeys.deck(deckId) });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create card',
        color: 'red',
      });
    },
  });
}

export function useCreateCardsBulk(deckId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cards: CardCreate[]) => flashcardApi.createCardsBulk(deckId, cards),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: flashcardKeys.cards(deckId) });
      queryClient.invalidateQueries({ queryKey: flashcardKeys.deck(deckId) });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to save cards',
        color: 'red',
      });
    },
  });
}

export function useUpdateCard(deckId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cardId, data }: { cardId: string; data: CardUpdate }) =>
      flashcardApi.updateCard(cardId, data),
    onSuccess: (_updated, variables) => {
      queryClient.invalidateQueries({ queryKey: flashcardKeys.card(variables.cardId) });
      queryClient.invalidateQueries({ queryKey: flashcardKeys.cards(deckId) });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update card',
        color: 'red',
      });
    },
  });
}

export function useDeleteCard(deckId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cardId: string) => flashcardApi.deleteCard(cardId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: flashcardKeys.cards(deckId) });
      queryClient.invalidateQueries({ queryKey: flashcardKeys.deck(deckId) });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete card',
        color: 'red',
      });
    },
  });
}

export function useReorderCards(deckId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cardIds: string[]) =>
      flashcardApi.reorderCards(deckId, cardIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: flashcardKeys.cards(deckId) });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to reorder cards',
        color: 'red',
      });
    },
  });
}

// ==================== Vocabulary Hooks ====================

export function useCreateVocabularyDeck() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: VocabularyDeckCreate) => flashcardApi.createVocabularyDeck(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: flashcardKeys.decks() });
      notifications.show({
        title: 'Success',
        message: 'Vocabulary deck created successfully',
        color: 'teal',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create vocabulary deck',
        color: 'red',
      });
    },
  });
}

export function usePreviewWords() {
  return useMutation({
    mutationFn: (words: string[]) => flashcardApi.previewWords(words),
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to preview words',
        color: 'red',
      });
    },
  });
}

// ==================== Study Session Hooks ====================

export function useStartSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: StartSessionRequest) => flashcardApi.startSession(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: flashcardKeys.sessions() });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to start study session',
        color: 'red',
      });
    },
  });
}

export function useSession(sessionId: string | undefined) {
  return useQuery({
    queryKey: flashcardKeys.session(sessionId!),
    queryFn: () => flashcardApi.getSession(sessionId!),
    enabled: !!sessionId,
    refetchInterval: 5000, // Refetch every 5 seconds during active session
  });
}

export function useSessionSummary(sessionId: string | undefined) {
  return useQuery({
    queryKey: [...flashcardKeys.session(sessionId!), 'summary'],
    queryFn: () => flashcardApi.getSessionSummary(sessionId!),
    enabled: !!sessionId,
  });
}

export function useCompleteSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => flashcardApi.completeSession(sessionId),
    onSuccess: (session, sessionId) => {
      queryClient.invalidateQueries({ queryKey: flashcardKeys.session(sessionId) });
      queryClient.invalidateQueries({ queryKey: flashcardKeys.sessions() });
      // Invalidate deck and progress queries since they will have changed after the session
      queryClient.invalidateQueries({ queryKey: flashcardKeys.deck(session.deck_id) });
      queryClient.invalidateQueries({ queryKey: flashcardKeys.progress(session.deck_id) });
      queryClient.invalidateQueries({ queryKey: flashcardKeys.cards(session.deck_id) });
      queryClient.invalidateQueries({ queryKey: flashcardKeys.decks() });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to complete session',
        color: 'red',
      });
    },
  });
}

// ==================== Progress Hooks ====================

export function useRecordAnswer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cardId, data }: { cardId: string; data: RecordAnswerRequest }) =>
      flashcardApi.recordAnswer(cardId, data),
    onSuccess: (_, { cardId, data }) => {
      queryClient.invalidateQueries({ queryKey: flashcardKeys.cardProgress(cardId) });
      queryClient.invalidateQueries({ queryKey: flashcardKeys.session(data.session_id) });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to record answer',
        color: 'red',
      });
    },
  });
}

export function useDeckProgress(deckId: string | undefined) {
  return useQuery({
    queryKey: flashcardKeys.progress(deckId!),
    queryFn: () => flashcardApi.getDeckProgress(deckId!),
    enabled: !!deckId,
  });
}

export function useCardProgress(cardId: string | undefined) {
  return useQuery({
    queryKey: flashcardKeys.cardProgress(cardId!),
    queryFn: () => flashcardApi.getCardProgress(cardId!),
    enabled: !!cardId,
  });
}

export function useCardsDueForReview(deckId?: string) {
  return useQuery({
    queryKey: flashcardKeys.dueCards(deckId),
    queryFn: () => flashcardApi.getCardsDueForReview(deckId),
  });
}

export function useUserStatistics() {
  return useQuery({
    queryKey: flashcardKeys.statistics(),
    queryFn: () => flashcardApi.getUserStatistics(),
  });
}
